import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/api-table.default.js';
import * as wireframeSkin from './skins/api-table.wireframe.js';
import '../table/run-table.js';
import '../table-head/run-table-head.js';
import '../table-body/run-table-body.js';
import '../table-row/run-table-row.js';
import '../table-cell/run-table-cell.js';
import '../table-column/run-table-column.js';
import '../badge/run-badge.js';
import '../text/run-text.js';
import '../stack/run-stack.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

export const ALL_SECTIONS = Object.freeze([
  'properties', 'slots', 'events', 'css-props', 'css-parts',
]);

const DEFAULT_SECTIONS = ALL_SECTIONS.join(',');

class RunApiTable extends RunElement {
  #contract  = null;
  #src       = null;
  #sections  = DEFAULT_SECTIONS;
  #loading   = false;
  #rendering = false;

  static get observedAttributes() {
    return ['src', 'sections', 'skin'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#src && !this.#contract) {
      this.#fetchContract(this.#src);
    } else {
      this.requestUpdate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'src':      this.src      = newValue; break;
      case 'sections': this.sections = newValue; break;
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  get contract() { return this.#contract; }
  set contract(value) {
    this.#contract = (value && typeof value === 'object') ? value : null;
    this.requestUpdate();
  }

  get src() { return this.#src; }
  set src(value) {
    const coerced = value ? String(value).trim() : null;
    if (this.#src === coerced) return;
    this.#src = coerced;
    if (coerced) {
      this.setAttribute('src', coerced);
      if (!this.#contract) this.#fetchContract(coerced);
    } else {
      this.removeAttribute('src');
    }
  }

  get sections() { return this.#sections; }
  set sections(value) {
    const coerced = value ? String(value).trim() : DEFAULT_SECTIONS;
    if (this.#sections === coerced) return;
    this.#sections = coerced;
    this.setAttribute('sections', coerced);
    this.requestUpdate();
  }

  get skin() { return this.getAttribute('skin'); }

  // ── Derived state exposed to skins ─────────────────────────────────────────

  get _activeSections() {
    return this.#sections
      .split(',')
      .map(s => s.trim())
      .filter(s => ALL_SECTIONS.includes(s));
  }

  get _loading() { return this.#loading; }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  // ── Private ────────────────────────────────────────────────────────────────

  async #fetchContract(url) {
    this.#loading = true;
    this.requestUpdate();
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.#contract = await response.json();
    } catch (err) {
      console.error(`[run-api-table] Failed to load contract from "${url}":`, err);
      this.#contract = null;
    } finally {
      this.#loading = false;
      this.requestUpdate();
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }
}

if (!customElements.get('run-api-table')) {
  customElements.define('run-api-table', RunApiTable);
}

export { RunApiTable };
export default RunApiTable;
